import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { isDisabled } from '../utils/customPropStyles';
import isChartPeriodSelected from '../utils/isChartPeriodSelected';
import Translate from './Translate';
import { AccessibilityStore, DataStore, EntityStore } from '../stores';
import { PeriodSelectorOptionWithDates } from '../models/components/ChartStore';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { action, computed } from 'mobx';
import { ChartStore } from '../stores';
import { Theme } from '../models/Theme';

const SelectorButton = styled<any, any>(
    'button',
    'selectorButton',
    'chartPeriodSelectorButton'
)`
    ${propStyles};
    ${isDisabled};
    outline: none;
`;

SelectorButton.defaultProps = {
    cursor: 'pointer',
    fontSize: 'xs',
    fontFamily: 'body',
    fontWeight: 'normal',
    paddingX: '1em',
    paddingY: '0.5em',
    bgColor: 'gray100',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray300',
    margin: '0 -1px 0 0',
    selected: {
        bgColor: 'primary',
        color: 'white',
    },
    isDisabled: {
        color: 'gray500',
    },
};

interface ChartPeriodSelectorButtonProps {
    theme?: Theme;
    accessibilityStore?: AccessibilityStore;
    dataStore?: DataStore;
    chartStore?: ChartStore;
    entityStore?: EntityStore;
    option: PeriodSelectorOptionWithDates;
    noDataForPeriodUi: string;
}

@inject('accessibilityStore', 'dataStore', 'entityStore')
@observer
export class ChartPeriodSelectorButton extends Component<
    ChartPeriodSelectorButtonProps
> {
    constructor(props: ChartPeriodSelectorButtonProps) {
        super(props);
    }

    @action
    handleOnClick = (option: PeriodSelectorOptionWithDates) => {
        const { setPeriod } = this.props.chartStore;
        const { dataStore, chartStore, entityStore } = this.props;
        setPeriod(option);

        dataStore.logDataLayer('chart_date_selection', {
            chart: chartStore.chartConfig.label,
            period: option.label,
            startDate: option.startDate,
            endDate: option.endDate,
            relatedEntityId: entityStore?.value?.clientCode,
        });
    };

    @computed
    get periodDisabled() {
        const { option, noDataForPeriodUi } = this.props;
        return !option.hasDataForPeriod && noDataForPeriodUi === 'disabled';
    }

    @computed
    get periodHidden() {
        const { option, noDataForPeriodUi } = this.props;
        return !option.hasDataForPeriod && noDataForPeriodUi === 'hidden';
    }

    @computed
    get periodSelected() {
        const { chartStore, option } = this.props;
        const { showSelectedPeriod } = chartStore;
        return showSelectedPeriod && isChartPeriodSelected(option);
    }

    @computed
    get chartId() {
        const { chartStore } = this.props;
        const { chartConfig } = chartStore;
        const { id } = chartConfig;
        return id;
    }

    render() {
        const {
            theme,
            chartStore,
            accessibilityStore,
            dataStore,
            option,
        } = this.props;

        if (!chartStore) {
            return null;
        }

        if (this.periodHidden) {
            return null;
        }

        return (
            <SelectorButton
                disabled={this.periodDisabled}
                data-selected={this.periodSelected && !this.periodDisabled}
                aria-disabled={this.periodDisabled}
                aria-pressed={this.periodSelected}
                aria-label={`${option.label} ${accessibilityStore.chartProperty(
                    this.chartId,
                    'periodDates',
                    {
                        startDate: accessibilityStore.formatDate(
                            option.startDate,
                            dataStore.culture
                        ),
                        endDate: accessibilityStore.formatDate(
                            option.endDate,
                            dataStore.culture
                        ),
                    }
                )}`}
                onClick={() => this.handleOnClick(option)}
                data-metric-id={`chartPeriodSelector-${
                    this.chartId
                }-${camelCase(option.label)}`}
                {...theme.ChartPeriodSelectorButton}
            >
                <Translate>{option.label}</Translate>
            </SelectorButton>
        );
    }
}

export default withTheme(ChartPeriodSelectorButton);



// WEBPACK FOOTER //
// ./src/Components/ChartPeriodSelectorButton.tsx